import React, { useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Skeleton, Chip, IconButton, Paper, TablePagination } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useCurrency, formatCurrency } from '../../../utils/currency';
import { extractTotalCount } from '../../../components/common/tableStyles';
import { InventoryService } from '../../../api/services/inventoryService';

export default function PurchaseOrdersTab({
    refreshKey = 0,
    getPOStatusColor,
    handleViewPO
}) {
    const { currency } = useCurrency();
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [purchaseOrdersLoading, setPurchaseOrdersLoading] = useState(false);
    const [totalPurchaseOrders, setTotalPurchaseOrders] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchPurchaseOrders = async () => {
        setPurchaseOrdersLoading(true);
        try {
            const data = await InventoryService.getPurchaseOrders({ page, limit: rowsPerPage });
            const dataArray = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setPurchaseOrders(dataArray);
            setTotalPurchaseOrders(extractTotalCount(data, dataArray));
        } catch (error) {
            console.error("Failed to fetch paginated purchase orders:", error);
        } finally {
            setPurchaseOrdersLoading(false);
        }
    };

    React.useEffect(() => {
        fetchPurchaseOrders();
    }, [page, rowsPerPage, refreshKey]);

    return (
        <Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>PO Number / Ref</TableCell>
                            <TableCell>Supplier / Vendor</TableCell>
                            <TableCell>Total Value</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Creation Date</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {purchaseOrdersLoading ? (
                            Array.from(new Array(5)).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell><Skeleton width="80%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell><Skeleton width="40%" /></TableCell>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell align="right"><Skeleton variant="circular" width={24} height={24} sx={{ display: 'inline-block' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : purchaseOrders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No purchase orders found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            purchaseOrders.map((po) => (
                                <TableRow 
                                    key={po.id} 
                                    hover 
                                    onClick={() => handleViewPO(po.id)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell><b>{po.poNumber || po.referenceCode || po.id}</b></TableCell>
                                    <TableCell>{po.supplierName || (po.supplier ? po.supplier.companyName || po.supplier.name : 'Unknown Vendor')}</TableCell>
                                    <TableCell><b>{formatCurrency(po.totalCost || po.totalAmount || po.totalValue || po.orderValue || 0, currency)}</b></TableCell>
                                    <TableCell><Chip label={po.status || 'Draft'} size="small" color={getPOStatusColor(po.status)} /></TableCell>
                                    <TableCell>{po.createdAt || po.issueDate ? new Date(po.createdAt || po.issueDate).toLocaleDateString() : 'N/A'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton 
                                            size="small" 
                                            color="info" 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewPO(po.id);
                                            }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={purchaseOrders.length === 0 ? 0 : totalPurchaseOrders}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
